/**
 * Customer-facing VirtualSpace tab / OG title helpers.
 *
 * Founder framing (AD #76 / migration 378): shareable VirtualSpaces
 * read as "VirtualSpace of [name]" unless the operator set an
 * explicit `seo_title`. Atomic Launches are often minted before the
 * deliverable has a `model_label`, so the row lands as the generic
 * fallback "Atomic VirtualSpace" while the site already has a real
 * location name + street address. After mint, As-Builts (and other
 * atomics) often take the catalog type stamp as `name` /
 * `model_label` — "Spatial As-Built" — which is not a place either.
 * Tab title and OG scrapers must prefer that place identity — never
 * the mint-time placeholder, never a catalog type stamp, and never a
 * raw point-cloud filename like `deer-trail.e57`.
 */

import {
  CUSTOMER_DELIVERABLE_LABELS,
  DELIVERABLE_TYPE_LABELS,
} from "@/lib/virtualspace/deliverable-types";
import { formatAddressSummary } from "@/lib/virtualspace/format-address";
import { placeTextIsRedundantWith } from "@/lib/virtualspace/notification-place";
import { formatScanFileNameForDisplay } from "@/lib/virtualspace/scan-display-name";
import { stateAbbreviationOrOriginal } from "@/lib/virtualspace/state-names";

/** Mint-time placeholder when `deliverables.model_label` is empty. */
export const ATOMIC_VIRTUAL_SPACE_FALLBACK_NAME = "Atomic VirtualSpace";

const FILE_LIKE_EXTENSION =
  /\.(e57|laz|las|copc|ply|xyz|pts|zip|copc\.laz|copc\.las)$/i;

const CATALOG_TYPE_STAMP_NAMES = new Set(
  [
    ...Object.values(DELIVERABLE_TYPE_LABELS),
    ...Object.values(CUSTOMER_DELIVERABLE_LABELS),
  ].map((label) => label.trim().toLowerCase()),
);

export function isGenericVirtualSpaceName(
  name: string | null | undefined,
): boolean {
  const trimmed = typeof name === "string" ? name.trim() : "";
  if (!trimmed) return true;
  return trimmed.toLowerCase() === ATOMIC_VIRTUAL_SPACE_FALLBACK_NAME.toLowerCase();
}

/** Operator / customer catalog labels such as "Spatial As-Built". */
export function isCatalogDeliverableTypeLabel(
  name: string | null | undefined,
): boolean {
  const trimmed = typeof name === "string" ? name.trim() : "";
  if (!trimmed) return false;
  return CATALOG_TYPE_STAMP_NAMES.has(trimmed.toLowerCase());
}

function isSkippableShareDisplayName(
  name: string | null | undefined,
): boolean {
  return isGenericVirtualSpaceName(name) || isCatalogDeliverableTypeLabel(name);
}

/** True when a label is (or was) a scan upload filename. */
export function looksLikeScanFileLabel(
  value: string | null | undefined,
): boolean {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) return false;
  if (FILE_LIKE_EXTENSION.test(trimmed)) return true;
  // After extension strip ("deer-trail.e57" → "deer-trail") still
  // reads as a slug filename, not a place.
  const cleaned = formatScanFileNameForDisplay(trimmed);
  if (!cleaned) return false;
  if (FILE_LIKE_EXTENSION.test(cleaned)) return true;
  // kebab/snake upload stems with no spaces are filenames, not sites.
  return /^[a-z0-9]+(?:[_-][a-z0-9]+)+$/i.test(cleaned) && !/\s/.test(cleaned);
}

function trimLabel(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().replace(/\s+/g, " ");
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Location name + street address for share titles. Drops the name
 * when it only restates the street (same rule as Matterport /
 * notification place copy).
 */
export function formatVirtualSpaceSharePlaceLabel(args: {
  locationName?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
}): string | null {
  const address =
    formatAddressSummary({
      addressLine1: args.addressLine1,
      addressLine2: args.addressLine2,
      city: args.city,
      state: stateAbbreviationOrOriginal(args.state) ?? undefined,
      postalCode: args.postalCode,
    }) || null;
  const locationNameRaw = trimLabel(args.locationName);
  const locationName =
    locationNameRaw &&
    (placeTextIsRedundantWith(locationNameRaw, args.addressLine1) ||
      placeTextIsRedundantWith(locationNameRaw, address))
      ? null
      : locationNameRaw;

  if (locationName && address) return `${locationName} · ${address}`;
  return locationName || address;
}

/**
 * Pick the customer-facing display name used inside
 * "VirtualSpace of …". Prefer a real operator VS name or the
 * site's location + address; skip catalog type stamps and never
 * lead with a point-cloud filename.
 */
export function resolveVirtualSpaceShareDisplayName(args: {
  name: string | null | undefined;
  placeLabel?: string | null;
  modelLabel?: string | null;
  spaceLabel?: string | null;
  /** Operator-set point-cloud display_name (friendly product name). */
  pointCloudDisplayName?: string | null;
  /** Raw source filename — last resort only, cleaned of extensions. */
  pointCloudFileName?: string | null;
  /**
   * @deprecated Prefer the structured fields above. Kept so older
   * callers that only had a single deliverable/point-cloud string
   * still compile during the transition.
   */
  deliverableLabel?: string | null;
  pointCloudLabel?: string | null;
}): string | null {
  const vsName = trimLabel(args.name);
  if (vsName && !isSkippableShareDisplayName(vsName)) return vsName;

  const place = trimLabel(args.placeLabel);
  if (place) return place;

  const model = trimLabel(args.modelLabel);
  if (
    model &&
    !looksLikeScanFileLabel(model) &&
    !isCatalogDeliverableTypeLabel(model)
  ) {
    return model;
  }

  const space = trimLabel(args.spaceLabel);
  if (
    space &&
    !looksLikeScanFileLabel(space) &&
    !isCatalogDeliverableTypeLabel(space)
  ) {
    return space;
  }

  // Legacy single-string deliverable label (Launch sibling cascade).
  const legacyDeliverable = trimLabel(args.deliverableLabel);
  if (
    legacyDeliverable &&
    !looksLikeScanFileLabel(legacyDeliverable) &&
    !isCatalogDeliverableTypeLabel(legacyDeliverable)
  ) {
    return legacyDeliverable;
  }

  const pcDisplay = trimLabel(args.pointCloudDisplayName);
  if (pcDisplay && !looksLikeScanFileLabel(pcDisplay)) return pcDisplay;

  const cleanedFile =
    formatScanFileNameForDisplay(args.pointCloudFileName) ||
    formatScanFileNameForDisplay(args.pointCloudLabel) ||
    null;
  if (cleanedFile) return cleanedFile;

  return vsName;
}

/**
 * Build the absolute tab / og:title string for a VirtualSpace share.
 * Operator `seo_title` wins as a full override; otherwise the
 * "VirtualSpace of …" umbrella prefix is applied. Avoid a redundant
 * "· VirtualSpace" suffix when the brand is already in the string.
 */
export function formatVirtualSpaceShareTitle(args: {
  seoTitle?: string | null;
  displayName?: string | null;
}): string {
  const seo = trimLabel(args.seoTitle);
  const baseTitle = seo
    ? seo
    : args.displayName
      ? `VirtualSpace of ${args.displayName}`
      : "VirtualSpace";
  return baseTitle.includes("VirtualSpace")
    ? baseTitle
    : `${baseTitle} · VirtualSpace`;
}
